import { Button, Heading, MAGNETIC_TOKENS } from '@appd/uik';
import { TimeRangeSelector } from '@appd/uik/@future';
import { useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { DashboardContext, useDashboardJson } from '../../../hooks';
import { setGlobals } from '../../../modules/components/DashboardViewer/globals';
import {
  cloneDashboard,
  getEffectiveTimeRangeFromGlobalsVariables,
  timerangePresets,
  variablesFromURLParams,
} from '../../../utils';
import { GLOBALS_VARIABLES_SINCE, GLOBALS_VARIABLES_UNTIL } from '../../../constants';

interface DashifyHeaderProps {
  isEditMode: boolean;
  isWidgetEditMode: boolean;
  title: string | undefined;
  setTitle: Dispatch<SetStateAction<string | undefined>>;
  enterEditMode: () => void;
  templateName: string;
}

const {
  COLORS: { CONTROL_BORDER },
} = MAGNETIC_TOKENS;

export const DashifyHeader: React.FC<DashifyHeaderProps> = ({
  templateName,
  isEditMode,
  isWidgetEditMode,
  title,
  setTitle,
  enterEditMode,
}) => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const variablesFromURL = variablesFromURLParams(router);
  const initialTimeRange = getEffectiveTimeRangeFromGlobalsVariables(variablesFromURL);
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRangeSelector.TimeRange.Value>({
    input: initialTimeRange.input,
    now: initialTimeRange.now,
  });
  const { addTemplate, UserDashboardPermissions } = useContext(DashboardContext);
  const { canCreateDashboards, canUpdateDashboards } = UserDashboardPermissions;
  const dashboard = useDashboardJson({ name: templateName });
  const showTimeRangeSelector = dashboard?.globals?.hideTimeRangeSelector !== true;
  const isSolutionDashboard = dashboard?.author === 'System';

  const onSelectedTimeRangeChange: TimeRangeSelector.Props['onSelectedTimeRangeChange'] = ({
    eventSource,
    value: {
      input: { since, until },
      now,
    },
  }) => {
    setSelectedTimeRange({
      input: {
        since,
        until,
      },
      now,
    });

    if (eventSource === 'REFRESH') {
      // reset `since` so widgets will re-render, otherwise refreshing with a very
      // similar time range/uql data does not trigger a visible refersh
      setGlobals(GLOBALS_VARIABLES_SINCE, null);
      queryClient.invalidateQueries();
    }

    // globals.variables.[since|until] will maintain absolute timerange
    setGlobals(GLOBALS_VARIABLES_SINCE, since);
    if (until) {
      setGlobals(GLOBALS_VARIABLES_UNTIL, until);
    }
  };

  useEffect(() => {
    const handleRouteChange = () => {
      const freshVariables = variablesFromURLParams(router);
      const freshTimeRange = getEffectiveTimeRangeFromGlobalsVariables(freshVariables);
      setSelectedTimeRange({
        input: freshTimeRange.input,
        now: freshTimeRange.now,
      });
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);

  const handleDuplicateClick = (): void => {
    const cloneWrapper = async (): Promise<void> => {
      if (dashboard) {
        const clonedDashboard = await cloneDashboard(dashboard);
        if (clonedDashboard) {
          addTemplate(clonedDashboard);
          router.push(`/${router.query.appName}/${clonedDashboard.name}/edit`);
          enterEditMode();
        }
      }
    };
    cloneWrapper().catch(() => {});
  };

  const handleEditClick = () => {
    router.push(`/${router.query.appName}/${templateName}/edit`);
    enterEditMode();
  };

  return (
    <div data-testid="dashify-header" className="flex items-center">
      <Heading data-testid="dashify-header-heading" size="PRIMARY">
        {isEditMode ? (
          <input
            data-testid="dashify-edit-mode-input-title"
            className="px-2 mt-4 border-2 rounded-md truncate"
            style={{ borderColor: CONTROL_BORDER }}
            onChange={e => setTitle(e.target.value)}
            placeholder={'Enter Title'}
            value={title}
          />
        ) : (
          title
        )}
      </Heading>

      <div className="flex ml-auto gap-4">
        {showTimeRangeSelector && (
          <TimeRangeSelector
            className="self-center"
            onSelectedTimeRangeChange={onSelectedTimeRangeChange}
            presets={timerangePresets}
            selectedTimeRange={selectedTimeRange}
          />
        )}
        {(canCreateDashboards || canUpdateDashboards) && !isEditMode && !isWidgetEditMode && (
          <div className="flex gap-1">
            {showTimeRangeSelector && (
              <div className="border-l-2 h-full mr-3" style={{ borderColor: CONTROL_BORDER }}></div>
            )}
            {canCreateDashboards && isSolutionDashboard && (
              <div style={{ marginTop: '3px' }}>
                <Button
                  data-testid="dashify-editor-edit-mode"
                  icon="Action.Duplicate"
                  type="SECONDARY"
                  onClick={handleDuplicateClick}
                >
                  Duplicate
                </Button>
              </div>
            )}
            {canUpdateDashboards && !isSolutionDashboard && (
              <div style={{ marginTop: '3px' }}>
                <Button
                  data-testid="dashify-editor-edit-mode"
                  icon="Action.Edit"
                  type="SECONDARY"
                  onClick={handleEditClick}
                >
                  Edit
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
